import {announce} from '@github-ui/aria-live'
import {PercentageCircle} from '@github-ui/percentage-circle'
import {Banner} from '@primer/react'
import {debounce} from 'lodash-es'
import {useEffect, useMemo, useRef} from 'react'

import {isAutoAddWorkflow} from '../helpers/workflow-utilities'
import {useProjectRouteParams} from '../router/use-project-route-params'
import {PROJECT_WORKFLOW_CLIENT_ID_ROUTE} from '../routes'
import {useBulkAddNotifications} from '../state-providers/bulk-add-notifications/bulk-add-notifications-state-provider'
import {useWorkflows} from '../state-providers/workflows/use-workflows'
import {BulkAddResources} from '../strings'
import styles from './bulk-add-notifications.module.css'

export const BulkAddNotifications = () => {
  const {bulkAddState, clearNotification} = useBulkAddNotifications()
  const {workflows} = useWorkflows()
  const projectRouteParams = useProjectRouteParams()

  const {percentage, totalItemsAdded, totalItemsFailed, isComplete, initialImport, isQueryBased} = bulkAddState ?? {}

  const autoAddWorkflow = useMemo(() => workflows.find(isAutoAddWorkflow), [workflows])

  const autoAddWorkflowUrl = useMemo(() => {
    if (!autoAddWorkflow) return
    return PROJECT_WORKFLOW_CLIENT_ID_ROUTE.generatePath({
      ...projectRouteParams,
      workflowClientId: autoAddWorkflow.clientId,
    })
  }, [autoAddWorkflow, projectRouteParams])

  const bannerProps = useMemo(() => {
    let variant: 'success' | 'critical' | 'info'
    let message: string
    let icon: React.ReactNode = undefined
    let dismissible = false
    // We only want to consider the bulk add failed if no items were added at all
    const failed = totalItemsAdded === 0 && totalItemsFailed !== 0
    // Success means items were added (import complete and not failed)
    const success = isComplete && !failed
    // No items matched the query (not a failure, just nothing to import)
    const noItems = isComplete && totalItemsAdded === 0 && totalItemsFailed === 0

    if (isComplete && failed) {
      variant = 'critical'
      message = BulkAddResources.failedMessage(totalItemsFailed ?? 0)
      dismissible = true
    } else if (noItems) {
      variant = 'info'
      message = BulkAddResources.noItemsMessage
      dismissible = true
    } else if (isComplete) {
      variant = 'success'
      message = BulkAddResources.completionMessage(totalItemsFailed ?? 0, !!initialImport, !!isQueryBased)
      dismissible = true
    } else if (percentage !== null && percentage !== undefined && percentage > 0) {
      variant = 'info'
      message = BulkAddResources.progressMessage(percentage)
      icon = <PercentageCircle radius={7} progress={percentage / 100.0} />
    } else {
      variant = 'info'
      message = BulkAddResources.startMessage
    }

    // Show workflow hint only for query-based imports on success
    const showWorkflowHint = isQueryBased && autoAddWorkflowUrl && success

    return {variant, message, icon, dismissible, showWorkflowHint}
  }, [isComplete, totalItemsAdded, totalItemsFailed, percentage, initialImport, isQueryBased, autoAddWorkflowUrl])

  const lastMessageRef = useRef('')

  const debounceAnnouncement = useMemo(
    () =>
      debounce(
        (announcement: string, assertive: boolean = false) => {
          announce(announcement, {assertive})
        },
        200,
        {trailing: true, leading: false},
      ),
    [],
  )

  useEffect(() => {
    return () => debounceAnnouncement.cancel()
  }, [debounceAnnouncement])

  useEffect(() => {
    if (bulkAddState && bannerProps.message !== lastMessageRef.current) {
      debounceAnnouncement(bannerProps.message, bannerProps.variant !== 'info')
      lastMessageRef.current = bannerProps.message
    }
  }, [debounceAnnouncement, bulkAddState, bannerProps])

  if (!bulkAddState) {
    return null
  }

  const workflowButton = bannerProps.showWorkflowHint && (
    <Banner.PrimaryAction as="a" href={autoAddWorkflowUrl}>
      {BulkAddResources.workflowButtonText}
    </Banner.PrimaryAction>
  )

  return (
    <Banner
      variant={bannerProps.variant}
      title={bannerProps.message}
      hideTitle
      onDismiss={bannerProps.dismissible ? clearNotification : undefined}
      className={styles.banner}
      icon={bannerProps.icon}
      primaryAction={workflowButton || undefined}
    >
      {bannerProps.message}
    </Banner>
  )
}
